'use client'

import { PictureTshirt24, Plus24 } from '@vinted/monochrome-icons'
import { Button, Cell, Icon, Label, Loader, Spacer, Text, Validation } from '@vinted/web-ui'
import { isEmpty, noop, take } from 'lodash'
import { ChangeEvent, Fragment, ReactNode, RefObject, useEffect, useRef, useState } from 'react'

import { PhotoTips } from '@marketplace-web/shared/photo-tips'
import { useTranslate } from '@marketplace-web/shared/i18n'
import { Dropzone, useLatestCallback } from '@marketplace-web/shared/ui-helpers'
import { UiState } from '@marketplace-web/shared/ui-state-data'
import { ItemAlertStatus } from '@marketplace-web/domain/item-data'

import { Screen } from 'constants/tracking/screens'
import { MediaUploadPhotoModel } from 'types/models/media-upload-photo'

import { AcceptedFileTypes, MAXIMUM_FILE_SIZE_BYTES } from '../../constants'
import useMediaSelectContext from '../../hooks/useMediaSelectContext'
import useMediaSelectSort from '../../hooks/useMediaSelectSort'
import useMediaSelectUtils from '../../hooks/useMediaSelectUtils'
import SortableList from '../SortableList'
import { MediaSelectType } from './constants'
import PhotoBox from './PhotoBox'

type Props = {
  tempUuid?: string
  numberOfImagesPerItem: number
  enableRotation?: boolean
  enablePhotoTips?: boolean
  enableDescription?: boolean
  type: MediaSelectType
  hideWhenEmpty?: boolean
  photoEntryId: number | string | null
  inputRef?: RefObject<HTMLInputElement | null>
  customValidationMessage?: string | null
  submitPhotoTips?: string | null
  title?: ReactNode
  uploadButtonText?: string
  onBlur?: () => void
  onFocus?: () => void
  onDelete?: (id: number) => void
  renderBanner?: () => ReactNode
  alertStatus?: ItemAlertStatus | null
  photoTipBanner?: {
    message: string
    actionTitle: string
  } | null
  screen?: Screen
  onUploadFilesStart?: () => void
  onUploadFilesEnd?: (uiState: UiState, photosCount: number, userItemCount: number) => void
}

const MediaSelect = ({
  tempUuid,
  numberOfImagesPerItem,
  enableRotation = false,
  enablePhotoTips = false,
  enableDescription = false,
  type,
  hideWhenEmpty = false,
  photoEntryId,
  inputRef,
  customValidationMessage,
  submitPhotoTips,
  title,
  onBlur = noop,
  onFocus = noop,
  onDelete = noop,
  uploadButtonText,
  renderBanner,
  alertStatus,
  photoTipBanner,
  screen = Screen.ItemUpload,
  onUploadFilesStart = noop,
  onUploadFilesEnd = noop,
}: Props) => {
  const translate = useTranslate('item_upload')
  const {
    photos,
    photoTipsStateWithSetter: [photoTips, setPhotoTips],
    uiStateWithSetter: [uiState, setUiState],
    photosCount,
    removePhoto,
    setPhotoOrientation,
  } = useMediaSelectContext()

  const [isControlsHidden, setIsControlsHidden] = useState(false)
  const [isFileSizeExceeded, setIsFileSizeExceeded] = useState(false)
  const [isPhotoTipsOpen, setIsPhotoTipsOpen] = useState(false)
  const [isNativeUploadOpen, setIsNativeUploadOpen] = useState(false)

  const containerRef = useRef<HTMLDivElement>(null)
  const localInputRef = useRef<HTMLInputElement>(null)
  const fileInputRef = inputRef || localInputRef
  const isUploading = uiState === UiState.Pending

  const { fetchPhotos, fetchPhotoTips, uploadPhotoFiles } = useMediaSelectUtils({
    photoEntryId,
    tempUuid,
    type,
    setUiState,
    photosCount,
    setPhotoTips,
    onUploadFilesStart,
    onUploadFilesEnd,
  })
  const { ariaText, handleSortStart, handleSortEnd, handleSortOver } = useMediaSelectSort({
    setIsControlsHidden,
    containerRef,
    photosCount,
    uploadSessionId: tempUuid,
    screen,
  })

  const latestOnBlur = useLatestCallback(onBlur)

  useEffect(() => {
    const handleWindowFocus = () => {
      if (!isNativeUploadOpen) return

      setIsNativeUploadOpen(false)
      latestOnBlur()
    }

    window.addEventListener('focus', handleWindowFocus)

    return () => window.removeEventListener('focus', handleWindowFocus)
  }, [isNativeUploadOpen, latestOnBlur])

  useEffect(() => {
    fetchPhotos()
    if (enablePhotoTips) fetchPhotoTips()
  }, [fetchPhotos, enablePhotoTips, fetchPhotoTips])

  const handleTipsOpen = () => setIsPhotoTipsOpen(true)

  const handleTipsClose = () => setIsPhotoTipsOpen(false)

  const showFileSelectionPrompt = () => {
    if (!fileInputRef.current) return

    setIsNativeUploadOpen(false)
    onFocus()
    fileInputRef.current.click()
  }

  const uploadFiles = (files: Array<File>) => {
    if (isUploading || files.length === 0) return

    if (numberOfImagesPerItem <= photosCount) return

    if (files.some(file => file.size > MAXIMUM_FILE_SIZE_BYTES)) {
      setIsFileSizeExceeded(true)

      return
    }

    setIsFileSizeExceeded(false)

    const filesToUpload = take(files, numberOfImagesPerItem - photosCount)

    uploadPhotoFiles({ files: filesToUpload, uploadType: type })

    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  const handlePhotoDelete = (id: number) => {
    if (!fileInputRef.current) return

    removePhoto(id)
    onDelete(id)
    fileInputRef.current.value = ''
  }

  const handleFileInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files) return

    const files = Array.from(event.target.files)

    uploadFiles(files)
  }

  const renderFileSizeError = () => {
    if (!isFileSizeExceeded) return null

    return <Validation text={translate('media_select.size_limit')} theme="warning" />
  }

  const renderCustomError = () => {
    if (!customValidationMessage) return null

    return <Validation text={customValidationMessage} theme="warning" />
  }

  const renderWithSpacer = (child: ReactNode) => {
    if (!child) return null

    return (
      <>
        <Spacer size={Spacer.Size.Medium} />
        {child}
      </>
    )
  }

  const renderNote = () => {
    if (alertStatus === ItemAlertStatus.ReplicaProof) {
      return (
        <Text
          as="h4"
          type={Text.Type.Caption}
          text={translate('media_select.replica_proof_label')}
        />
      )
    }

    if (photosCount < 2) return null

    return (
      <Text as="h4" type={Text.Type.Caption} text={translate('media_select.reordering_label')} />
    )
  }

  const renderPhotoTips = () => {
    if (!submitPhotoTips) return null

    return <Text as="h4" type={Text.Type.Caption} text={submitPhotoTips} />
  }

  const renderUploadButtonBox = () => {
    if (photosCount === numberOfImagesPerItem) return null

    if (isUploading) {
      return (
        <div data-testid="upload-button-loader" className="media-select__input-box">
          <div className="media-select__input-box-content">
            <Loader size={Loader.Size.XLarge} />
          </div>
        </div>
      )
    }

    if (photosCount < 1) return null

    return (
      <div data-testid="add-photos-icon-button" className="media-select__input-box">
        <div className="media-select__input-box-content">
          <Button
            onClick={showFileSelectionPrompt}
            icon={<Icon name={Plus24} testId="plus" color={Icon.Color.Primary} />}
            inline
            aria={{
              'aria-label': translate('media_select.a11y.add_photo'),
            }}
          />
        </div>
      </div>
    )
  }

  const renderUploadButtonArea = () => {
    if (photosCount > 0 || isUploading) return null

    return (
      <div className="media-select__input">
        <div className="media-select__input-content">
          <Button
            text={uploadButtonText || translate('media_select.upload_button')}
            onClick={showFileSelectionPrompt}
            icon={<Icon name={Plus24} testId="plus" color={Icon.Color.Primary} />}
            inline
          />
        </div>
      </div>
    )
  }

  const renderPhotoBox = (photo: MediaUploadPhotoModel, index: number) => (
    <Fragment key={photo.id}>
      <PhotoBox
        setPhotoOrientation={setPhotoOrientation}
        index={index}
        photo={photo}
        onDelete={handlePhotoDelete}
        isControlsHidden={isControlsHidden || isUploading}
        enableRotation={enableRotation}
        imageAlt={`${translate('media_select.a11y.uploaded_image', {
          count: index + 1,
          total: photosCount,
        })} ${translate('media_select.a11y.inactive_reorder')}`}
      />
      <span aria-live="assertive" className="u-visually-hidden">
        {ariaText}
      </span>
    </Fragment>
  )

  const renderPhotoTipsAction = () => (
    <>
      &nbsp;
      <button type="button" onClick={handleTipsOpen}>
        <Text
          as="span"
          type={Text.Type.Subtitle}
          text={translate('photo_tips.see_tips')}
          clickable
        />
      </button>
    </>
  )

  const renderDescription = () => {
    if (!enablePhotoTips && !enableDescription) return null

    return (
      <span>
        {enableDescription && (
          <Text
            type={Text.Type.Subtitle}
            as="span"
            text={translate('media_select.description', {
              max_number_of_files: numberOfImagesPerItem,
            })}
          />
        )}
        {enablePhotoTips && renderPhotoTipsAction()}
      </span>
    )
  }

  const renderImageGrid = () => (
    <Dropzone onDrop={uploadFiles}>
      <SortableList
        testId="media-select-grid"
        className="media-select__grid"
        onDragStart={handleSortStart}
        onDragOver={handleSortOver}
        onSortUpdate={handleSortEnd}
      >
        {photos.map(renderPhotoBox)}
        {renderUploadButtonBox()}
      </SortableList>

      {renderUploadButtonArea()}
    </Dropzone>
  )

  const renderImageInput = () => (
    <input
      data-testid="add-photos-input"
      name="photos"
      ref={fileInputRef}
      onChange={handleFileInputChange}
      className="u-hidden"
      type="file"
      accept={AcceptedFileTypes.Images}
      multiple
    />
  )

  const hidden = hideWhenEmpty && isEmpty(photos)

  if (hidden) {
    // we cannot render null, because <input /> could be triggered from forwarded ref
    return renderImageInput()
  }

  const renderPhotoTipsBanner = () => {
    if (!photoTipBanner) return null

    return (
      <div className="photo-tips__banner">
        <Cell
          styling={Cell.Styling.Narrow}
          prefix={
            <>
              <Spacer orientation={Spacer.Orientation.Horizontal} size={Spacer.Size.Small} />
              <Icon name={PictureTshirt24} color={Icon.Color.Primary} />
            </>
          }
          body={
            <>
              <Text text={photoTipBanner.message} as="span" type={Text.Type.Subtitle} />
              &nbsp;
              <button type="button" onClick={handleTipsOpen}>
                <Text
                  theme="primary"
                  as="span"
                  type={Text.Type.Subtitle}
                  text={photoTipBanner.actionTitle}
                  clickable
                />
              </button>
            </>
          }
          theme="calypsoLightExperimental"
        />
      </div>
    )
  }

  return (
    <div data-testid="media-select" id="photos" ref={containerRef}>
      {renderImageInput()}

      {!photoTipBanner && (
        <Label type={Label.Type.Leading} styling={Label.Styling.Wide} text={renderDescription()} />
      )}

      <Cell styling={Cell.Styling.Wide}>
        {title || null}
        {renderImageGrid()}

        {renderWithSpacer(
          renderFileSizeError() || renderCustomError() || renderNote() || renderPhotoTips() || null,
        )}

        {renderWithSpacer(renderPhotoTipsBanner())}

        <PhotoTips
          isOpen={isPhotoTipsOpen}
          photoTips={photoTips}
          handleClose={handleTipsClose}
          uploadSessionId={tempUuid}
        />
        {renderBanner?.()}
      </Cell>
    </div>
  )
}

export default MediaSelect
